import { normalizeCargoPricing } from './cargoPricing';

export const DELIVERY_METHODS = {
  pickup: {
    id: 'pickup',
    enabled: true,
    minTotal: 0,
    requiresAddress: false,
    regions: [],
    label: ''
  },
  cargo: {
    id: 'cargo',
    enabled: true,
    minTotal: 0,
    requiresAddress: true,
    regions: [],
    label: ''
  },
  own_vehicle_istanbul: {
    id: 'own_vehicle_istanbul',
    enabled: true,
    minTotal: 12000,
    requiresAddress: true,
    regions: [],
    label: ''
  },
  own_vehicle_regional: {
    id: 'own_vehicle_regional',
    enabled: true,
    minTotal: 30000,
    requiresAddress: true,
    regions: ['Çerkezköy', 'Çorlu', 'Gebze'],
    label: ''
  }
};

export const DELIVERY_METHOD_IDS = Object.keys(DELIVERY_METHODS);

export const DEFAULT_DELIVERY_SETTINGS = {
  defaultMethod: 'cargo',
  methods: DELIVERY_METHOD_IDS.map((id) => ({ ...DELIVERY_METHODS[id] })),
  freeShippingEnabled: false,
  freeShippingMinTotal: 0,
  cargoPricing: normalizeCargoPricing()
};

export const normalizeDeliverySettings = (raw = {}) => {
  const configured = Array.isArray(raw?.methods) ? raw.methods : [];
  const methods = DELIVERY_METHOD_IDS.map((id) => {
    const fallback = DELIVERY_METHODS[id];
    const match = configured.find((item) => item?.id === id);
    return {
      id,
      enabled: match?.enabled !== false,
      minTotal: Math.max(0, Number(match?.minTotal ?? fallback.minTotal) || 0),
      requiresAddress: match?.requiresAddress !== undefined
        ? Boolean(match.requiresAddress)
        : fallback.requiresAddress,
      regions: Array.isArray(match?.regions)
        ? match.regions.map((region) => String(region || '').trim()).filter(Boolean)
        : [...fallback.regions],
      label: String(match?.label || '').trim()
    };
  });

  const freeShippingEnabled = raw?.freeShippingEnabled === true
    || raw?.cargoPricing?.freeShippingEnabled === true;
  const freeShippingMinTotal = Math.max(
    0,
    Number(raw?.freeShippingMinTotal ?? raw?.cargoPricing?.freeShippingMinTotal) || 0
  );

  return {
    defaultMethod: DELIVERY_METHOD_IDS.includes(raw?.defaultMethod) ? raw.defaultMethod : 'cargo',
    methods,
    freeShippingEnabled,
    freeShippingMinTotal,
    cargoPricing: normalizeCargoPricing({
      ...(raw?.cargoPricing || {}),
      freeShippingEnabled,
      freeShippingMinTotal
    })
  };
};

export const getResolvedDeliveryMethods = (deliverySettings) => {
  const settings = normalizeDeliverySettings(deliverySettings);
  return settings.methods.filter((method) => method.enabled);
};

export const getDeliveryMethodMeta = (methodId, deliverySettings) => {
  const methods = getResolvedDeliveryMethods(deliverySettings);
  const fromSettings = methods.find((method) => method.id === methodId);
  if (fromSettings) {
    return fromSettings;
  }
  return DELIVERY_METHODS[methodId] || null;
};

export const getActiveDeliveryMethodIds = (deliverySettings) =>
  getResolvedDeliveryMethods(deliverySettings).map((method) => method.id);

/** cartTotal must be VAT-inclusive product total (not excl. + VAT on top of the threshold). */
export const isDeliveryMethodAvailable = (methodId, cartTotal, deliverySettings) => {
  const meta = getDeliveryMethodMeta(methodId, deliverySettings);
  if (!meta) return false;
  return Number(cartTotal || 0) >= meta.minTotal;
};

export const deliveryRequiresAddress = (methodId, deliverySettings) =>
  Boolean(getDeliveryMethodMeta(methodId, deliverySettings)?.requiresAddress);

const DEFAULT_LABEL_MAP = {
  pickup: 'Gel-Al',
  cargo: 'Kargo ile teslim',
  own_vehicle_istanbul: 'İstanbul içi kendi aracımızla teslim',
  own_vehicle_regional: 'İstanbul, Gebze, Çorlu, Çerkezköy\'e kendi araçlarımız ile teslimat'
};

export const formatDeliveryNote = ({ method, region, address, deliverySettings }) => {
  const lines = [];
  const meta = getDeliveryMethodMeta(method, deliverySettings);
  const label = meta?.label || DEFAULT_LABEL_MAP[method] || method;
  lines.push(`Teslimat: ${label}`);
  if (region) {
    lines.push(`Bölge: ${region}`);
  }
  if (address && (address.street || address.city)) {
    const parts = [
      address.neighborhood,
      address.district,
      address.city,
      [address.street, address.buildingNo ? `No:${address.buildingNo}` : '', address.apartmentNo ? `D:${address.apartmentNo}` : ''].filter(Boolean).join(' '),
      address.zipCode,
      address.country
    ].filter(Boolean);
    if (parts.length) {
      lines.push(`Adres: ${parts.join(', ')}`);
    }
  }
  return lines.join('\n');
};
